import { FC, ComponentPropsWithoutRef } from 'react';
import { css } from '@emotion/react';
import { isNumber } from 'lodash';
import { calculateChangeColor } from '../../styles';
import formatDisplay, { FormatterStyle } from '../../utils/formatDisplay';
import { TimeRange } from '../../types';

interface Props extends ComponentPropsWithoutRef<'span'> {
  value?: number;
  prefix?: string;
  timeRange?: TimeRange | undefined;
  animated?: boolean; // Enable animated number display
  customDisplay?: string; // Custom text to display instead of formatted percent
}

const timeRangeDisplay: Record<TimeRange, string> = {
  [TimeRange.days1]: '',
  [TimeRange.weeks1]: 'last week',
  [TimeRange.months1]: 'last month',
  [TimeRange.months3]: 'last 3 mo',
  [TimeRange.months6]: 'last 6 mo',
  [TimeRange.ytd]: 'YTD',
  [TimeRange.years1]: 'last year',
  [TimeRange.years5]: 'last 5 yrs',
};

// Mix a color with white background at specified alpha to create solid color
const mixColorWithWhite = (color: string, alpha: number): string => {
  let r: number, g: number, b: number;

  // Parse RGB from color string like "rgb(24, 157, 75)"
  const rgbMatch = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
  if (rgbMatch) {
    [, r, g, b] = rgbMatch.map(Number);
  } else {
    // Parse hex color like "#74726D"
    const hexMatch = color.match(/^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i);
    if (!hexMatch) return color;

    [, r, g, b] = hexMatch.slice(1).map((hex) => parseInt(hex, 16));
  }

  const bg = [255, 255, 255]; // white background
  const mixed = [r, g, b].map((c, i) => Math.round(alpha * c + (1 - alpha) * bg[i]));
  return `rgb(${mixed[0]}, ${mixed[1]}, ${mixed[2]})`;
};

const ColoredPercentChange: FC<Props> = ({
  value,
  prefix,
  timeRange,
  animated = false, // eslint-disable-line @typescript-eslint/no-unused-vars
  customDisplay,
  ...props
}) => {
  const baseColor = isNumber(value) ? calculateChangeColor(value) : 'inherit';
  const backgroundColor = isNumber(value) ? mixColorWithWhite(baseColor, 0.1) : 'inherit';

  return (
    <span
      css={css`
        color: ${baseColor};
        background-color: ${backgroundColor};
        padding: 4px 8px 4px 8px;
        border-radius: 4px;
      `}
      {...props}
    >
      {prefix && `${prefix} `}
      {customDisplay ?? formatDisplay(FormatterStyle.percentChange, value)}
      {timeRange && timeRangeDisplay[timeRange] && ` ${timeRangeDisplay[timeRange]}`}
    </span>
  );
};

export default ColoredPercentChange;
